import React, { useEffect, useMemo, useState, useCallback, memo, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { CheckCircleTwoTone, ClockCircleOutlined, TrophyOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';

import { TOTAL_ROUNDS_MAX } from '../../constants/game';
import { routeNames } from '../../constants/routes';
import useGetRandomUserToken from '../../hooks/useGetRandomUserToken';
import { setIsRoundActive } from '../../redux/actions/battle';
import { setLastResult } from '../../redux/actions/result';
import { updateBattle, updateBattlePlayer } from '../../services/firebase';
import { findUserFromBattleByRandomTokenId, getUnixTimestamp } from '../../util';

// Type assertion for JS action creators
const setIsRoundActiveAction = setIsRoundActive as unknown as (payload: { roundId: number; active: boolean }) => any;
const setLastResultAction = setLastResult as unknown as (payload: any) => any;

// ============================================
// DESIGN TOKENS
// ============================================
const tokens = {
    colors: {
        surface: 'rgba(18, 18, 20, 0.95)',
        surfaceLight: 'rgba(255, 255, 255, 0.05)',
        border: 'rgba(255, 255, 255, 0.1)',
        text: '#ffffff',
        textMuted: 'rgba(255, 255, 255, 0.6)',
        textDim: 'rgba(255, 255, 255, 0.4)',
        accent: '#c80707',
        accentGlow: 'rgba(200, 7, 7, 0.4)',
        success: '#22c55e',
        warning: '#f59e0b',
        gold: '#fbbf24',
        silver: '#9ca3af',
        bronze: '#cd7c2f',
    },
    blur: '24px',
    radius: '16px',
    radiusSmall: '10px',
    shadow: '0 12px 48px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
};

// ============================================
// ANIMATIONS
// ============================================
const fadeInLeft = keyframes`
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
`;

const countdownPulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================
const HUDContainer = styled.div`
    position: fixed;
    top: 80px;
    left: 20px;
    z-index: 100;
    width: 280px;
    animation: ${fadeInLeft} 0.5s ease-out;

    @media (max-width: 960px) {
        display: none;
    }
`;

const Panel = styled.div`
    background: ${tokens.colors.surface};
    backdrop-filter: blur(${tokens.blur});
    -webkit-backdrop-filter: blur(${tokens.blur});
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radius};
    box-shadow: ${tokens.shadow};
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid ${tokens.colors.border};
    background: rgba(0, 0, 0, 0.2);
`;

const RoundBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const RoundNumber = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, ${tokens.colors.accent} 0%, #a00606 100%);
    border-radius: 10px;
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 15px;
    font-weight: 700;
    color: ${tokens.colors.text};
    box-shadow: 0 4px 12px ${tokens.colors.accentGlow};
`;

const RoundInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

const RoundLabel = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${tokens.colors.textMuted};
`;

const RoundText = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: ${tokens.colors.text};
`;

const StatusBadge = styled.div<{ $active?: boolean }>`
    padding: 5px 10px;
    border-radius: 20px;
    font-family: 'Work Sans', sans-serif;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: ${props => props.$active
        ? 'rgba(34, 197, 94, 0.2)'
        : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$active
        ? tokens.colors.success
        : tokens.colors.textMuted};
    animation: ${props => props.$active ? pulse : 'none'} 2s ease-in-out infinite;
`;

// Status Bar for countdown and messages
const StatusBar = styled.div<{ $type?: 'countdown' | 'info' | 'success' | 'waiting' }>`
    padding: 10px 16px;
    background: ${props => {
        switch (props.$type) {
            case 'countdown': return 'rgba(245, 158, 11, 0.15)';
            case 'success': return 'rgba(34, 197, 94, 0.15)';
            case 'info': return 'rgba(59, 130, 246, 0.15)';
            default: return 'rgba(255, 255, 255, 0.05)';
        }
    }};
    border-bottom: 1px solid ${tokens.colors.border};
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StatusIcon = styled.div<{ $type?: 'countdown' | 'info' | 'success' | 'waiting' }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${props => {
        switch (props.$type) {
            case 'countdown': return 'rgba(245, 158, 11, 0.3)';
            case 'success': return 'rgba(34, 197, 94, 0.3)';
            case 'info': return 'rgba(59, 130, 246, 0.3)';
            default: return 'rgba(255, 255, 255, 0.1)';
        }
    }};
    color: ${props => {
        switch (props.$type) {
            case 'countdown': return tokens.colors.warning;
            case 'success': return tokens.colors.success;
            case 'info': return '#3b82f6';
            default: return tokens.colors.textMuted;
        }
    }};
    font-size: 14px;

    ${props => props.$type === 'countdown' && css`
        animation: ${countdownPulse} 1s ease-in-out infinite;
    `}
`;

const StatusText = styled.div`
    flex: 1;
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    color: ${tokens.colors.text};
    line-height: 1.4;
`;

const StatusHighlight = styled.span<{ $color?: string }>`
    font-weight: 700;
    color: ${props => props.$color || tokens.colors.warning};
`;

const CountdownNumber = styled.span`
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 14px;
    color: ${tokens.colors.warning};
    background: rgba(245, 158, 11, 0.2);
    padding: 2px 8px;
    border-radius: 6px;
    margin-left: 4px;
`;

const PlayersSection = styled.div`
    padding: 10px 12px;
    max-height: 220px;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${tokens.colors.border};
        border-radius: 2px;
    }
`;

const SectionTitle = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${tokens.colors.textDim};
    margin-bottom: 8px;
    padding-left: 4px;
`;

const PlayerRow = styled.div<{ $isMe?: boolean; $rank?: number }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    margin-bottom: 4px;
    background: ${props => props.$isMe
        ? 'rgba(200, 7, 7, 0.15)'
        : tokens.colors.surfaceLight};
    border: 1px solid ${props => props.$isMe
        ? 'rgba(200, 7, 7, 0.3)'
        : 'transparent'};
    border-radius: ${tokens.radiusSmall};
    transition: all 0.2s ease;

    &:last-child {
        margin-bottom: 0;
    }
`;

const RankIndicator = styled.div<{ $rank: number }>`
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: ${props => {
        if (props.$rank === 1) return 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)';
        if (props.$rank === 2) return 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)';
        if (props.$rank === 3) return 'linear-gradient(135deg, #cd7c2f 0%, #b45309 100%)';
        return 'rgba(255, 255, 255, 0.1)';
    }};
    color: ${props => props.$rank <= 3 ? '#000' : tokens.colors.textMuted};
`;

const PlayerInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const PlayerName = styled.div<{ $isMe?: boolean }>`
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    font-weight: ${props => props.$isMe ? '600' : '500'};
    color: ${tokens.colors.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PlayerScore = styled.div`
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: ${tokens.colors.textMuted};
    margin-top: 1px;
`;

const PlayerStatus = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
`;

const GuessingDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${tokens.colors.warning};
    animation: ${pulse} 1.5s ease-in-out infinite;
`;

const MyScoreSection = styled.div`
    padding: 12px 16px;
    border-top: 1px solid ${tokens.colors.border};
    background: rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const MyScoreLabel = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${tokens.colors.textMuted};
`;

const MyScoreValue = styled.div`
    font-family: 'JetBrains Mono', monospace;
    font-size: 20px;
    font-weight: 700;
    color: ${tokens.colors.text};
    letter-spacing: -0.02em;
`;

const MyScoreUnit = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${tokens.colors.textMuted};
    margin-left: 4px;
`;

const ActionSection = styled.div`
    padding: 12px 16px;
    border-top: 1px solid ${tokens.colors.border};
    background: rgba(0, 0, 0, 0.1);
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' }>`
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    ${props => props.$variant === 'success' ? css`
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        color: ${tokens.colors.text};
        box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
        }
    ` : css`
        background: linear-gradient(135deg, ${tokens.colors.accent} 0%, #a00606 100%);
        color: ${tokens.colors.text};
        box-shadow: 0 4px 12px ${tokens.colors.accentGlow};

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px ${tokens.colors.accentGlow};
        }
    `}

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }
`;

const ActionHint = styled.p`
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    color: ${tokens.colors.textMuted};
    text-align: center;
    margin: 8px 0 0 0;
`;

const ResultLink = styled(Link)`
    color: inherit;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
`;

// ============================================
// TYPES
// ============================================
interface Player {
    name: string;
    odhadnuto?: boolean;
    score?: number;
    odhadnutiScore?: number;
    odhadnutí?: boolean;
    odhadnutíScore?: number;
    odhadnutiVCase?: boolean;
    odhadnutíVCase?: boolean;
    odhadnutiVCasScore?: number;
    odhadnutíVCasScore?: number;
    odhadnutiVCasScoreRound?: number;
    odhadnutíVCasScoreRound?: number;
    isGuessing?: boolean;
    hasGuessed?: boolean;
    roundScore?: number;
    totalScore?: number;
    isMe?: boolean;
    odhadnutiVCasScoreRoundRound?: number;
    odhadnutíVCasScoreRoundRound?: number;
    odhadnutiVCasScoreRoundRoundRound?: number;
    odhadnutíVCasScoreRoundRoundRound?: number;
}

interface BattleHUDProps {
    round: number;
    totalRounds: number;
    players: Player[];
    myTotalScore: number;
    isRoundActive: boolean;
    myPlayerId?: string;
}

// ============================================
// MEMOIZED SUB-COMPONENTS
// ============================================

interface PlayerRowItemProps {
    player: {
        name: string;
        isMe?: boolean;
        calculatedScore: number;
        hasGuessedCurrentRound: boolean;
    };
    rank: number;
}

const PlayerRowItem = memo<PlayerRowItemProps>(({ player, rank }) => (
    <PlayerRow $isMe={player.isMe} $rank={rank}>
        <RankIndicator $rank={rank}>
            {rank}
        </RankIndicator>
        <PlayerInfo>
            <PlayerName $isMe={player.isMe}>
                {player.name}
                {player.isMe && ' (ty)'}
            </PlayerName>
            <PlayerScore>
                {player.calculatedScore} bodů
            </PlayerScore>
        </PlayerInfo>
        <PlayerStatus title={player.hasGuessedCurrentRound ? 'Tip umístěn' : 'Hádá...'}>
            {player.hasGuessedCurrentRound ? (
                <CheckCircleTwoTone twoToneColor="#22c55e" />
            ) : (
                <GuessingDot />
            )}
        </PlayerStatus>
    </PlayerRow>
));

PlayerRowItem.displayName = 'PlayerRowItem';

interface PlayersListProps {
    sortedPlayers: Array<{
        name: string;
        isMe?: boolean;
        calculatedScore: number;
        hasGuessedCurrentRound: boolean;
    }>;
}

const PlayersList = memo<PlayersListProps>(({ sortedPlayers }) => (
    <PlayersSection>
        <SectionTitle>Hráči ({sortedPlayers.length})</SectionTitle>
        {sortedPlayers.map((player, index) => (
            <PlayerRowItem
                key={`${player.name}-${index}`}
                player={player}
                rank={index + 1}
            />
        ))}
    </PlayersSection>
));

PlayersList.displayName = 'PlayersList';

// ============================================
// COUNTDOWN STATUS BAR - completely isolated, manages its own state
// ============================================
interface CountdownStatusBarProps {
    isActive: boolean;
    startTime: number | null; // Unix timestamp when countdown started
    countdownDuration: number; // Total countdown time in seconds
    firstName: string | null;
    isMyGuess: boolean;
    onComplete: () => void;
}

const CountdownStatusBar: React.FC<CountdownStatusBarProps> = memo(({
    isActive,
    startTime,
    countdownDuration,
    firstName,
    isMyGuess,
    onComplete,
}) => {
    const [timeLeft, setTimeLeft] = useState<number>(-1);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const onCompleteRef = useRef(onComplete);
    const hasCompletedRef = useRef(false);

    // Keep onComplete ref up to date without causing re-renders
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    // Calculate and manage countdown
    useEffect(() => {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!isActive || !startTime) {
            setTimeLeft(-1);
            hasCompletedRef.current = false;
            return;
        }

        // Calculate initial time left
        const calculateTimeLeft = () => {
            const elapsed = Math.floor((Date.now() / 1000) - startTime);
            return Math.max(0, countdownDuration - elapsed);
        };

        setTimeLeft(calculateTimeLeft());
        hasCompletedRef.current = false;

        // Start interval
        intervalRef.current = setInterval(() => {
            const remaining = calculateTimeLeft();
            setTimeLeft(remaining);

            if (remaining <= 0 && !hasCompletedRef.current) {
                hasCompletedRef.current = true;
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                onCompleteRef.current();
            }
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isActive, startTime, countdownDuration]);

    if (!isActive || timeLeft < 0) {
        return null;
    }

    return (
        <StatusBar $type="countdown">
            <StatusIcon $type="countdown">
                <ClockCircleOutlined />
            </StatusIcon>
            <StatusText>
                {isMyGuess ? (
                    <>Nejrychlejší tip! Zbývá<CountdownNumber>{timeLeft}s</CountdownNumber></>
                ) : (
                    <>
                        <StatusHighlight>{firstName}</StatusHighlight> byl první. Zbývá
                        <CountdownNumber>{timeLeft}s</CountdownNumber>
                    </>
                )}
            </StatusText>
        </StatusBar>
    );
});

CountdownStatusBar.displayName = 'CountdownStatusBar';

// ============================================
// COMPONENT
// ============================================
export const BattleHUD: React.FC<BattleHUDProps> = ({
    round,
    totalRounds,
    players,
    myTotalScore,
    isRoundActive: isRoundActiveProp,
    myPlayerId,
}) => {
    const dispatch = useDispatch();
    const { battleId } = useParams<{ battleId: string }>();
    const randomUserToken = useGetRandomUserToken();
    const currentBattleInfo = useSelector((state: any) => state.battle.currentBattle);
    const currentBattlePlayers = useSelector((state: any) => state.battle.currentBattle.players);
    const [isStartingNextRound, setIsStartingNextRound] = useState(false);

    const { isGameStarted, countdown, rounds, createdById, myNickname } = currentBattleInfo;
    const isBattleCreator = createdById === randomUserToken;

    // Memoized countdown info to prevent unnecessary re-renders
    const countdownInfo = useMemo(() => {
        if (!rounds || !rounds[round - 1]) {
            return { isActive: false, startTime: null, firstName: null, isMyGuess: false };
        }

        const currentRound = rounds[round - 1];
        const { isGuessed, guessedTime, firstGuess, isRoundActive } = currentRound;

        if (firstGuess?.guessedById && isGuessed && isRoundActive && guessedTime) {
            return {
                isActive: true,
                startTime: guessedTime,
                firstName: firstGuess.name,
                isMyGuess: firstGuess.guessedById === randomUserToken,
            };
        }

        return { isActive: false, startTime: null, firstName: null, isMyGuess: false };
    }, [rounds, round, randomUserToken]);

    // Handle countdown completion - end the round
    const handleCountdownComplete = useCallback(() => {
        dispatch(
            setIsRoundActiveAction({
                roundId: round,
                active: false,
            })
        );
    }, [dispatch, round]);

    // Also check if all players have guessed (end round early)
    useEffect(() => {
        if (currentBattlePlayers !== null && countdownInfo.isActive) {
            const playersWithFinishedRoundGuess = currentBattlePlayers.filter(
                (player: any) => player[`round${round}`]
            );
            if (playersWithFinishedRoundGuess.length === currentBattlePlayers.length) {
                handleCountdownComplete();
            }
        }
    }, [currentBattlePlayers, round, countdownInfo.isActive, handleCountdownComplete]);

    const handleStartNextRound = async () => {
        if (!battleId || isStartingNextRound) return;

        setIsStartingNextRound(true);
        try {
            // Update nickname if changed
            if (myNickname) {
                await updateBattlePlayer(battleId, randomUserToken, {
                    name: myNickname,
                });
            }

            // Start next round
            await updateBattle(battleId, {
                currentRoundStart: getUnixTimestamp(new Date()),
                round: round + 1,
            });
        } catch (err) {
            console.error('Start next round error:', err);
        } finally {
            setIsStartingNextRound(false);
        }
    };

    const handleShowResults = () => {
        dispatch(
            setLastResultAction({
                players: currentBattleInfo.players,
                round: currentBattleInfo.round,
                rounds: currentBattleInfo.rounds,
                totalScore: currentBattleInfo.myTotalScore,
                mode: currentBattleInfo.mode,
                city: currentBattleInfo.selectedCity,
                radius: currentBattleInfo.radius,
                isBattle: true,
            })
        );
    };

    // Calculate total score from individual rounds (round1, round2, etc.)
    const calculatePlayerTotalScore = useCallback((player: any): number => {
        let total = 0;
        for (let i = 1; i <= TOTAL_ROUNDS_MAX; i++) {
            const roundData = player[`round${i}`];
            if (roundData?.score) {
                total += Math.round(roundData.score);
            }
        }
        return total;
    }, []);

    // Check if player has guessed in current round
    const hasPlayerGuessedCurrentRound = useCallback((player: any): boolean => {
        const roundData = player[`round${round}`];
        return typeof roundData?.score !== 'undefined';
    }, [round]);

    // Sort players by score for ranking - MEMOIZED to prevent blinking on countdown tick
    const sortedPlayers = useMemo(() => {
        return [...players]
            .map(player => ({
                ...player,
                calculatedScore: calculatePlayerTotalScore(player),
                hasGuessedCurrentRound: hasPlayerGuessedCurrentRound(player),
            }))
            .sort((a, b) => b.calculatedScore - a.calculatedScore);
    }, [players, calculatePlayerTotalScore, hasPlayerGuessedCurrentRound]);

    // Determine if we should show the action button
    const shouldShowActionButton = useMemo(() => {
        if (!rounds || !rounds[round - 1]) return false;

        const currentRound = rounds[round - 1];
        const { isGuessed, isRoundActive } = currentRound;

        // Show button when round is finished (all guessed, countdown done)
        return isGuessed && !isRoundActive;
    }, [rounds, round]);

    const isLastRound = round >= TOTAL_ROUNDS_MAX;

    // Get status bar content (excluding countdown - that's handled by CountdownStatusBar)
    const statusBarContent = useMemo(() => {
        if (!isGameStarted) {
            return {
                type: 'info' as const,
                icon: <ClockCircleOutlined />,
                message: (
                    <>
                        Po prvním tipu začne odpočet <StatusHighlight>{countdown}s</StatusHighlight>
                    </>
                ),
            };
        }

        if (currentBattlePlayers !== null && rounds && rounds[round - 1]) {
            const currentRound = rounds[round - 1];
            const { isGuessed, firstGuess, isRoundActive } = currentRound;
            const battleUserCreator = findUserFromBattleByRandomTokenId(currentBattlePlayers, createdById);

            // Game ended
            if (firstGuess?.guessedById && isGuessed && !isRoundActive && round >= TOTAL_ROUNDS_MAX) {
                return {
                    type: 'success' as const,
                    icon: <TrophyOutlined />,
                    message: <StatusHighlight $color={tokens.colors.success}>Hra skončila!</StatusHighlight>,
                };
            }

            // Round ended, waiting for next
            if (firstGuess?.guessedById && isGuessed && !isRoundActive) {
                return {
                    type: 'info' as const,
                    icon: <ClockCircleOutlined />,
                    message: (
                        <>
                            <StatusHighlight $color="#3b82f6">{battleUserCreator?.name}</StatusHighlight> spustí {round + 1}. kolo
                        </>
                    ),
                };
            }

            // Countdown is running - don't show static status bar, CountdownStatusBar handles this
            if (countdownInfo.isActive) {
                return null;
            }
        }

        return null;
    }, [
        isGameStarted,
        countdown,
        currentBattlePlayers,
        rounds,
        round,
        createdById,
        countdownInfo.isActive,
    ]);

    return (
        <HUDContainer>
            <Panel>
                {/* Header with round info */}
                <Header>
                    <RoundBadge>
                        <RoundNumber>{round}</RoundNumber>
                        <RoundInfo>
                            <RoundLabel>Kolo</RoundLabel>
                            <RoundText>{round} / {totalRounds}</RoundText>
                        </RoundInfo>
                    </RoundBadge>
                    <StatusBadge $active={isRoundActiveProp}>
                        {isRoundActiveProp ? 'Hádá se' : 'Pauza'}
                    </StatusBadge>
                </Header>

                {/* Status bar with static messages */}
                {statusBarContent && (
                    <StatusBar $type={statusBarContent.type}>
                        <StatusIcon $type={statusBarContent.type}>
                            {statusBarContent.icon}
                        </StatusIcon>
                        <StatusText>{statusBarContent.message}</StatusText>
                    </StatusBar>
                )}

                {/* Countdown status bar - isolated component with its own state */}
                <CountdownStatusBar
                    isActive={countdownInfo.isActive}
                    startTime={countdownInfo.startTime}
                    countdownDuration={countdown}
                    firstName={countdownInfo.firstName}
                    isMyGuess={countdownInfo.isMyGuess}
                    onComplete={handleCountdownComplete}
                />

                {/* Players list - memoized to prevent re-render on countdown tick */}
                <PlayersList sortedPlayers={sortedPlayers} />

                {/* My score summary */}
                <MyScoreSection>
                    <MyScoreLabel>Tvoje skóre</MyScoreLabel>
                    <MyScoreValue>
                        {Math.round(myTotalScore)}
                        <MyScoreUnit>bodů</MyScoreUnit>
                    </MyScoreValue>
                </MyScoreSection>

                {/* Action button for creator */}
                {shouldShowActionButton && (
                    <ActionSection>
                        {isLastRound ? (
                            // Show results button (for everyone after last round)
                            <ActionButton $variant="success" onClick={handleShowResults}>
                                <ResultLink to={`/${routeNames.vysledek}`}>
                                    <TrophyOutlined />
                                    Zobrazit výsledky
                                </ResultLink>
                            </ActionButton>
                        ) : isBattleCreator ? (
                            // Next round button (only for creator)
                            <>
                                <ActionButton
                                    onClick={handleStartNextRound}
                                    disabled={isStartingNextRound}
                                >
                                    <PlayCircleOutlined />
                                    {isStartingNextRound ? 'Spouštím...' : `Spustit ${round + 1}. kolo`}
                                </ActionButton>
                                <ActionHint>Odstartuj další kolo</ActionHint>
                            </>
                        ) : (
                            // Waiting message for non-creators
                            <ActionHint style={{ margin: 0 }}>
                                Čekám na spuštění dalšího kola...
                            </ActionHint>
                        )}
                    </ActionSection>
                )}
            </Panel>
        </HUDContainer>
    );
};

export default BattleHUD;
